import React, { useState } from 'react';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import Container from '@amzn/awsui-components-console/container';
import FormField from '@amzn/awsui-components-console/form-field';
import Grid from '@amzn/awsui-components-console/grid';
import Header from '@amzn/awsui-components-console/header';
import Icon from '@amzn/awsui-components-console/icon';
import Select, { SelectProps } from '@amzn/awsui-components-console/select';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { ExternalLinkItem } from '../../components/commonComponents/ExternalLinkItem';
import {
	CARD_SVGS,
	SnowMascot,
	HeroSnowflakes,
} from './SNOWHomeIllustrations';
import {
	NAVIGATION_CARDS,
	ENTITY_TYPES,
	WORKFLOW_ROUTES,
	DASHBOARD_ROUTES,
	DASHBOARD_OPTIONS,
	DEFAULT_WORKFLOW_URL,
} from './SNOWHomeConstants';
import './SNOWHome.scss';

export default function SNOWHome() {
	const [selectedOption, setSelectedOption] =
		useState<SelectProps.Option | null>(null);
	const [selectedOptionDashboard, setSelectedOptionDashboard] =
		useState<SelectProps.Option | null>(null);

	const nextStepClicked = () => {
		const key = selectedOption?.value;
		const route = (key && WORKFLOW_ROUTES[key]) || DEFAULT_WORKFLOW_URL;

		if (route.startsWith('#')) location.href = route;
		else window.open(route, '_blank', 'noopener,noreferrer');
	};

	const nextStepClickedDashboard = () => {
		const key = selectedOptionDashboard?.value;

		if (!key) return;
		const route = DASHBOARD_ROUTES[key];

		if (!route) return;
		if (route.startsWith('#')) location.href = route;
		else window.open(route, '_blank', 'noopener,noreferrer');
	};

	return (
		<Box margin={{ bottom: 'l' }}>
			<div className="snow-home__hero">
				<HeroSnowflakes />
				<Box padding={{ vertical: 'xxl', horizontal: 'xl' }}>
					<Grid
						gridDefinition={[
							{ colspan: { xxs: 12, s: 6 } },
							{ colspan: { xxs: 12, s: 6 } },
						]}
					>
						<SpaceBetween size="xs">
							<h1 className="snow-home__hero-title">
								Simple Node Ordering Workflow
							</h1>
							<p className="snow-home__hero-subtitle">
								Design workflows with drag-and-drop, execute in bulk, and track
								progress in real time. Automate the orchestration and
								configuration of services required for launching, reconfiguring,
								or deprecating nodes across Amazon Fulfillment Network.
							</p>
							<Box padding={{ top: 's' }}>
								<SpaceBetween direction="horizontal" size="s">
									<Button variant="primary" href="#/executeWorkflows">
										Execute Workflows
									</Button>
									<Button href="#/entitySearch">Monitor Nodes</Button>
								</SpaceBetween>
							</Box>
						</SpaceBetween>
						<div className="snow-home__hero-illustration">
							<SnowMascot />
						</div>
					</Grid>
				</Box>
			</div>

			<Box padding={{ horizontal: 'xl', top: 'l', bottom: 'l' }}>
				<SpaceBetween size="s">
					<Box variant="h2" padding="n">
						Get started
					</Box>
					<div className="snow-home__cards-grid">
						<ColumnLayout columns={4}>
							{NAVIGATION_CARDS.map((card) => (
								<div key={card.id} className="snow-home__card">
									<a href={`#${card.href}`} className="snow-home__card-link">
										<div
											className="snow-home__card-banner"
											style={{ background: card.gradient }}
										>
											{CARD_SVGS[card.id]}
										</div>
										<div className="snow-home__card-body">
											<Box variant="h3" padding="n">
												{card.label}
											</Box>
											<Box
												variant="p"
												color="text-body-secondary"
												fontSize="body-s"
												padding={{ top: 'xxs' }}
											>
												{card.description}
											</Box>
											<Box padding={{ top: 's' }}>
												<span
													className="snow-home__card-cta"
													role="presentation"
												>
													Get started
												</span>
											</Box>
										</div>
									</a>
								</div>
							))}
						</ColumnLayout>
					</div>
				</SpaceBetween>
			</Box>

			<Box padding={{ top: 'l', horizontal: 'xl' }}>
				<Grid
					gridDefinition={[
						{ colspan: { xxs: 12, s: 8 } },
						{ colspan: { xxs: 12, s: 4 } },
					]}
				>
					<SpaceBetween size="l">
						<Container
							header={
								<Header
									variant="h2"
									description="What makes SNOW the go-to platform for node automation"
								>
									❄ Benefits and features
								</Header>
							}
						>
							<Box padding={{ top: 's' }}>
								<ColumnLayout columns={2}>
								<SpaceBetween size="xxs">
									<SpaceBetween direction="horizontal" size="xs">
										<Icon name="share" size="small" />
										<Box variant="h4" padding="n">
											DAG Workflow Engine
										</Box>
									</SpaceBetween>
									<Box variant="p" color="text-body-secondary">
										Design interdependent tasks as a Directed Acyclic Graph
										for complex SOPs with conditional execution paths.
									</Box>
								</SpaceBetween>
								<SpaceBetween size="xxs">
									<SpaceBetween direction="horizontal" size="xs">
										<Icon name="check" size="small" />
										<Box variant="h4" padding="n">
											Ready-made task library
										</Box>
									</SpaceBetween>
									<Box variant="p" color="text-body-secondary">
										SIM/TT creation, CNames, LDAP groups, SNS/Email
										notifications and more, available at day 1 for any
										business.
									</Box>
								</SpaceBetween>
								<SpaceBetween size="xxs">
									<SpaceBetween direction="horizontal" size="xs">
										<Icon name="refresh" size="small" />
										<Box variant="h4" padding="n">
											Continuously updated
										</Box>
									</SpaceBetween>
									<Box variant="p" color="text-body-secondary">
										SNOW evolves with downstream node setup services.
										Automations stay current as requirements change.
									</Box>
								</SpaceBetween>
								<SpaceBetween size="xxs">
									<SpaceBetween direction="horizontal" size="xs">
										<Icon name="status-positive" size="small" />
										<Box variant="h4" padding="n">
											Defined SLAs
										</Box>
									</SpaceBetween>
									<Box variant="p" color="text-body-secondary">
										SLA-backed workflow tasks let launch PMs work backwards
										and plan node requests with confidence.
									</Box>
								</SpaceBetween>
							</ColumnLayout>
							</Box>
						</Container>
						<Container
							header={
								<Header
									variant="h2"
									description="How teams use SNOW to streamline operations"
								>
									❄ Use cases
								</Header>
							}
						>
							<Box padding={{ top: 's' }}>
								<ColumnLayout columns={2}>
								<SpaceBetween size="xxs">
									<SpaceBetween direction="horizontal" size="xs">
										<Icon name="caret-right-filled" size="small" />
										<Box variant="h4" padding="n">
											Automate node launches
										</Box>
									</SpaceBetween>
									<Box variant="p" color="text-body-secondary">
										Replace manual SOP steps with automated workflows. Onboard
										your business and let SNOW handle execution.
									</Box>
								</SpaceBetween>
								<SpaceBetween size="xxs">
									<SpaceBetween direction="horizontal" size="xs">
										<Icon name="edit" size="small" />
										<Box variant="h4" padding="n">
											Adapt to changing needs
										</Box>
									</SpaceBetween>
									<Box variant="p" color="text-body-secondary">
										Modify workflows and add new tasks as business
										requirements evolve. No rebuilds needed.
									</Box>
								</SpaceBetween>
								<SpaceBetween size="xxs">
									<SpaceBetween direction="horizontal" size="xs">
										<Icon name="lock-private" size="small" />
										<Box variant="h4" padding="n">
											Abstract service complexity
										</Box>
									</SpaceBetween>
									<Box variant="p" color="text-body-secondary">
										Teams only need to know SNOW. No permissions or deep
										knowledge of individual node setup services required.
									</Box>
								</SpaceBetween>
								<SpaceBetween size="xxs">
									<SpaceBetween direction="horizontal" size="xs">
										<Icon name="view-full" size="small" />
										<Box variant="h4" padding="n">
											Real-time execution tracking
										</Box>
									</SpaceBetween>
									<Box variant="p" color="text-body-secondary">
										Replace spreadsheets and SIMs with a live dashboard that
										tracks every workflow step in real time.
									</Box>
								</SpaceBetween>
							</ColumnLayout>
							</Box>
						</Container>
					</SpaceBetween>
					<SpaceBetween size="s">
						<Container header={<Header variant="h3">Create New Entity</Header>}>
							<SpaceBetween size="m">
								<Button variant="primary" href="#/executeWorkflows">
									Execute Self-Serve Workflows
								</Button>
								<FormField label="Or select a legacy workflow" stretch>
									<Select
										selectedAriaLabel="Selected"
										options={ENTITY_TYPES}
										selectedOption={selectedOption}
										placeholder="Choose a workflow"
										onChange={(e) => setSelectedOption(e.detail.selectedOption)}
									/>
								</FormField>
								{selectedOption && (
									<Button onClick={nextStepClicked} variant="primary">
										Start Workflow
									</Button>
								)}
							</SpaceBetween>
						</Container>
						<Container header={<Header variant="h3">Dashboards</Header>}>
							<SpaceBetween size="m">
								<FormField stretch>
									<Select
										placeholder="Select a dashboard"
										selectedAriaLabel="Selected"
										options={DASHBOARD_OPTIONS}
										selectedOption={selectedOptionDashboard}
										ariaRequired={true}
										onChange={({ detail }) =>
											setSelectedOptionDashboard(detail.selectedOption)
										}
									/>
								</FormField>
								{selectedOptionDashboard && (
									<Button onClick={nextStepClickedDashboard} variant="primary">
										Go to Dashboard
									</Button>
								)}
							</SpaceBetween>
						</Container>
						<Container
							header={
								<Header variant="h3">
									Getting started{' '}
									<span role="img" aria-label="Icon external Link">
										<Icon name="external" size="inherit" />
									</span>
								</Header>
							}
						>
							<ul
								aria-label="Getting started documentation"
								className="custom-list-separator"
							>
								<li>
									<ExternalLinkItem
										href="https://w.amazon.com/bin/view/SNOWHome"
										text="What is SNOW?"
									/>
								</li>
								<li>
									<ExternalLinkItem
										href="https://w.amazon.com/bin/view/SNOW/UserGuide"
										text="User Guide"
									/>
								</li>
								<li>
									<ExternalLinkItem
										href="https://w.amazon.com/bin/view/SNOW/Permissions/"
										text="Permissions"
									/>
								</li>
							</ul>
						</Container>
						<Container
							header={
								<Header variant="h3">
									More resources{' '}
									<span role="img" aria-label="Icon external Link">
										<Icon name="external" size="inherit" />
									</span>
								</Header>
							}
						>
							<ul
								aria-label="Additional resource links"
								className="custom-list-separator"
							>
								<li>
									<ExternalLinkItem
										href="https://w.amazon.com/bin/view/SNOW/FAQs"
										text="FAQ"
									/>
								</li>
								<li>
									<ExternalLinkItem
										href="https://river.amazon.com/HYD13/workflows?buildingType=SNOW&q0=SNOW%20report%20Issue&id=9cffdb1b-0d0a-42f5-8988-94c41bf465f8"
										text="Report an issue"
									/>
								</li>
								<li>
									<ExternalLinkItem
										href="https://snow.aft.amazon.dev/#/Contact"
										text="Contact us"
									/>
								</li>
							</ul>
						</Container>
					</SpaceBetween>
				</Grid>
			</Box>
		</Box>
	);
}
